import React, { useContext, useMemo } from 'react';
import ActionTypes from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import rolesConstants from '@utils/rolesConstants';
import { DatePicker, Form, FormInstance, Radio } from 'antd';
import moment, { Moment } from 'moment';
import SessionContext from '@providers/Session/SessionContext';

type AlgarSuspensionActionTypes =
  | ActionTypes.CONTRACT_SUSPEND
  | ActionTypes.PROVISIONAL_WITHDRAW;

interface AlgarSuspensionPayloadProps {
  action: AlgarSuspensionActionTypes;
  devices: {
    msisdn: string;
  }[];
  suspensionDays: number | string;
}

interface AlgarSuspensionProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useAlgarSuspension = ({
  devices,
  form,
  isUpload,
}: AlgarSuspensionProps): DeviceActionProps => {
  const { t } = useTranslation(['actions-modal', 'device-actions']);
  const { isMot } = useContext(SessionContext);

  const actionKey = Form.useWatch<AlgarSuspensionActionTypes>(
    'actionKey',
    form,
  );
  const suspensionDays = Form.useWatch<number>('suspensionDays', form);
  const withdrawDate = Form.useWatch<Moment>('withdrawDate', form);

  const payloadToSend = useMemo((): AlgarSuspensionPayloadProps => {
    let treatedSuspensionDays: number | string = suspensionDays;

    if (actionKey === ActionTypes.PROVISIONAL_WITHDRAW && withdrawDate) {
      treatedSuspensionDays = withdrawDate?.format('YYYY-MM-DD');
    }

    return {
      action: actionKey,
      devices: isUpload
        ? devices
        : devices?.map(device => ({
            msisdn: device.msisdn,
          })),
      suspensionDays: treatedSuspensionDays,
    };
  }, [suspensionDays, actionKey, isUpload, devices, withdrawDate]);

  const disabledDate = (date: Moment) => {
    const inputDate = moment(date);
    const currentDate = moment().startOf('day');

    return inputDate.isBefore(currentDate);
  };

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const isWithdraw = actionKey === ActionTypes.PROVISIONAL_WITHDRAW;
    return [
      {
        title: t('block-unlock.action'),
        name: 'actionKey',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button">
            <Radio value={ActionTypes.CONTRACT_SUSPEND}>
              {t('suspension-withdrawal.suspend')}
            </Radio>
            <Radio value={ActionTypes.PROVISIONAL_WITHDRAW}>
              {t('suspension-withdrawal.withdrawal')}
            </Radio>
          </Radio.Group>
        ),
      },
      {
        title: t(
          `suspension-withdrawal.${isWithdraw ? 'withdraw-in' : 'suspend-for'}`,
        ),
        showWhenUpload: true,
        name: isWithdraw ? 'withdrawDate' : 'suspensionDays',
        content: isWithdraw ? (
          <DatePicker
            format="DD/MM/YYYY"
            disabledDate={disabledDate}
            style={{
              width: '100%',
            }}
          />
        ) : (
          <Radio.Group optionType="button">
            {[30, 60, 90, 120].map(days => (
              <Radio key={days} value={days}>
                {suspensionDays === days
                  ? t('suspension-withdrawal.days-in-suspension', {
                      days,
                    })
                  : days}
              </Radio>
            ))}
          </Radio.Group>
        ),
      },
    ];
  }, [actionKey, suspensionDays, t]);

  return {
    steps,
    payloadToSend,
    uploadPosition: 1,
    title: t(`device-actions:labels.mot.${rolesConstants.CONTRACT_SUSPEND}`),
    infoMessage: (
      <div>
        {actionKey === ActionTypes.CONTRACT_SUSPEND && (
          <span>
            {t('suspension-withdrawal.description')}
            &nbsp;
          </span>
        )}
        {isMot && (
          <span style={{ opacity: 1, fontWeight: 'bold' }}>
            {t('just-algar-lines')?.toUpperCase()}
          </span>
        )}
      </div>
    ),
  };
};

export default useAlgarSuspension;
